import { useEffect, useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import api from "../utils/axios";
import formatCurrency from "../helper/formatCurrency";

interface DepositBonusItem {
  deposit_no: number;
  minimum_deposit: number;
  bonus_percent: number;
}





const DepositBonus = () => {
  const [depositBonuses, setDepositBonuses] = useState<DepositBonusItem[]>([]);
  const [newBonus, setNewBonus] = useState<DepositBonusItem>({
    deposit_no: 0,
    minimum_deposit: 0,
    bonus_percent: 0,
  });
  const [editBonus, setEditBonus] = useState<DepositBonusItem | null>(null);

  /* ================= FETCH ================= */
 const fetchDepositBonuses = async () => {
  try {
    const response = await api.get('/getDepositBonus');

    if (response.data?.data) {
      const sortedData = response.data.data.sort(
        (a, b) => a.deposit_no - b.deposit_no
      );

      setDepositBonuses(sortedData);
    } else {
      toast('Failed to fetch deposit bonuses.');
    }
  } catch (error) {
    console.error('Error fetching deposit bonuses:', error);
    toast('Error fetching deposit bonuses.');
  }
};


  useEffect(() => {
    fetchDepositBonuses();
  }, []);

  /* ================= ADD ================= */
  const handleAddBonus = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newBonus.minimum_deposit || !newBonus.bonus_percent) {
      toast.warning("All fields are required");
      return;
    }

    try {
      await api.post("/createDepositBonus", newBonus);
      toast.success("Deposit bonus added");
      setNewBonus({ deposit_no: 0, minimum_deposit: 0, bonus_percent: 0 });
      fetchDepositBonuses();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Error adding bonus");
    }
  };

  /* ================= UPDATE ================= */
  const handleUpdateBonus = async () => {
    if (!editBonus) return;

    try {
      await api.post(
        `/updateDepositBonus/${editBonus.deposit_no}`,
        editBonus
      );
      toast.success("Deposit bonus updated");
      setEditBonus(null);
      fetchDepositBonuses();
    } catch {
      toast.error("Failed to update bonus");
    }
  };

  /* ================= DELETE ================= */
  const handleDeleteBonus = async (deposit_no: number) => {
    if (!window.confirm("Delete this bonus?")) return;

    try {
      await api.delete(`/deleteDepositBonus/${deposit_no}`);
      toast.success("Deposit bonus deleted");
      fetchDepositBonuses();
    } catch {
      toast.error("Failed to delete bonus");
    }
  };

  return (
    <div className="space-y-8">
      {/* ================= HEADER ================= */}
      <div>
        <h1 className="text-2xl font-bold text-gray-800">
          Deposit Bonus Settings
        </h1>
        <p className="text-sm text-gray-500">
          Configure bonus percentages per deposit count
        </p>
      </div>

      {/* ================= ADD BONUS ================= */}
      <div className="bg-white border rounded-xl p-6 shadow-sm">
        <h2 className="text-lg font-semibold mb-4">Add New Bonus</h2>

        <form
          onSubmit={handleAddBonus}
          className="grid grid-cols-1 md:grid-cols-3 gap-4"
        >
          <input
            type="number"
            placeholder="Deposit No"
            value={newBonus.deposit_no}
            onChange={(e) =>
              setNewBonus({ ...newBonus, deposit_no: +e.target.value })
            }
            className="border rounded-lg px-3 py-2"
          />

          <input
            type="number"
            placeholder="Minimum Deposit"
            value={newBonus.minimum_deposit}
            onChange={(e) =>
              setNewBonus({ ...newBonus, minimum_deposit: +e.target.value })
            }
            className="border rounded-lg px-3 py-2"
          />

          <input
            type="number"
            placeholder="Bonus %"
            value={newBonus.bonus_percent}
            onChange={(e) =>
              setNewBonus({ ...newBonus, bonus_percent: +e.target.value })
            }
            className="border rounded-lg px-3 py-2"
          />

          <div className="md:col-span-3">
            <button className="px-6 py-2 bg-gray-900 text-white rounded-lg">
              Add Bonus
            </button>
          </div>
        </form>
      </div>

      {/* ================= BONUS TABLE ================= */}
      <div className="bg-white border rounded-xl shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              <th className=" px-4 py-3 text-left">#</th>
              <th className="px-4 py-3 text-left">Deposit</th>
              <th className="px-4 py-3 text-left">Min Deposit</th>
              <th className="px-4 py-3 text-left">Bonus %</th>
              <th className="px-4 py-3 text-left">Actions</th>
            </tr>
          </thead>

          <tbody>
            {depositBonuses.length === 0 ? (
              <tr>
                <td colSpan={4} className="py-8 text-center text-gray-500">
                  No bonuses found
                </td>
              </tr>
            ) : (
              depositBonuses.map((b, index) => (
                <tr
                  key={b.deposit_no}
                  className="border-t hover:bg-gray-50"
                >

                   <td className=" px-4 py-2">
                      {index + 1}
                    </td>
                  <td className="px-4 py-3 font-medium">
                    {b.deposit_no} Deposit
                  </td>

                  <td className="px-4 py-3">
                    {""}   {editBonus?.deposit_no === b.deposit_no ? (
                      <input
                        type="number"
                        value={editBonus.minimum_deposit}
                        onChange={(e) =>
                          setEditBonus({
                            ...editBonus,
                            minimum_deposit: +e.target.value,
                          })
                        }
                        className="border rounded px-2 py-1 w-28"
                      />
                    ) :  ( 
                      formatCurrency(b.minimum_deposit)
                    )}
                  </td>

                  <td className="px-4 py-3">
                    {editBonus?.deposit_no === b.deposit_no ? (
                      <input
                        type="number"
                        value={editBonus.bonus_percent}
                        onChange={(e) =>
                          setEditBonus({
                            ...editBonus,
                            bonus_percent: +e.target.value,
                          })
                        }
                        className="border rounded px-2 py-1 w-20"
                      />
                    ) : (
                      `${b.bonus_percent}%`
                    )}
                  </td>

                  <td className="px-4 py-3 flex gap-2">
                    {editBonus?.deposit_no === b.deposit_no ? (
                      <>
                        <button
                          onClick={handleUpdateBonus}
                          className="px-3 py-1 bg-green-600 text-white rounded text-xs"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => setEditBonus(null)}
                          className="px-3 py-1 border rounded text-xs"
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => setEditBonus(b)}
                          className="px-3 py-1 bg-yellow-500 text-white rounded text-xs"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteBonus(b.deposit_no)}
                          className="px-3 py-1 bg-red-600 text-white rounded text-xs"
                        >
                          Delete
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <ToastContainer />
    </div>
  );
};

export default DepositBonus;
